/**
 * Scènes de mise en situation à l'échelle réelle (« chez toi »).
 *
 * Chaque scène est une pièce dessinée en SVG (cf. ScenePreview.svelte) avec
 * une référence d'échelle familière (lampe, canapé, porte). La règle d'or :
 * l'échelle est EXACTE — chaque scène fixe son `pxPerCm` et le kit est
 * dimensionné avec. C'est tout l'intérêt : aider à se figurer la taille
 * réelle du kit avant de commander.
 *
 * AUCUN DOM ici : uniquement des specs (viewBox, px/cm, zone d'accroche) et
 * des fonctions pures testables. Le dessin vit dans le composant.
 */

export type SceneKey = 'desk' | 'living' | 'wall';

/** En dessous de 45 cm de large : scène « bureau ». */
export const DESK_MAX_CM = 45;
/** De 45 à 150 cm : scène « salon ». Au-delà : « grand mur ». */
export const LIVING_MAX_CM = 150;

export type SceneAnchor = {
  /**
   * 'rest' : kit posé, appuyé au mur — `y` est le bord BAS du kit
   * (la surface du bureau). 'hang' : kit accroché — `y` est son centre vertical.
   */
  mode: 'rest' | 'hang';
  /** Centre horizontal du kit dans le viewBox (px). */
  centerX: number;
  /** Bord bas ('rest') ou centre vertical ('hang') du kit (px). */
  y: number;
  /** Largeur max affichable sans déborder de la scène (px). */
  maxW: number;
  /** Hauteur max affichable (px). */
  maxH: number;
};

export type SceneSpec = {
  key: SceneKey;
  viewBox: { width: number; height: number };
  /** Échelle de la scène : combien de px du viewBox pour 1 cm réel. */
  pxPerCm: number;
  anchor: SceneAnchor;
  /** Référence d'échelle affichée dans la scène (libellé UI, fr, zéro jargon). */
  reference: { label: string };
  /** Complément d'aria-label (« posé sur un bureau »…). */
  ariaContext: string;
  /** Bascule 3D légère appliquée par le composant (deg) — 0 : aucune. */
  tiltDeg: number;
};

/**
 * Les trois scènes. Repères physiques (validés par les tests) :
 * - desk   : 4 px/cm — bureau h 75 cm (surface y=316, sol y=616), lampe 40 cm.
 * - living : 3 px/cm — canapé 220 cm de large (x 150→810), h 80 cm, sol y=690.
 * - wall   : 2,2 px/cm — porte 200 cm (y 170→610), silhouette 170 cm,
 *            le kit peut monter à 250 cm de large à l'échelle exacte (550 px).
 */
export const SCENES: Record<SceneKey, SceneSpec> = {
  desk: {
    key: 'desk',
    viewBox: { width: 900, height: 640 },
    pxPerCm: 4,
    anchor: { mode: 'rest', centerX: 490, y: 316, maxW: 400, maxH: 256 },
    reference: { label: 'Lampe : 40 cm' },
    ariaContext: 'posé sur un bureau',
    tiltDeg: 3,
  },
  living: {
    key: 'living',
    viewBox: { width: 960, height: 720 },
    pxPerCm: 3,
    anchor: { mode: 'hang', centerX: 480, y: 210, maxW: 600, maxH: 360 },
    reference: { label: 'Canapé : 2,20 m' },
    ariaContext: 'accroché au-dessus d’un canapé',
    tiltDeg: 0,
  },
  wall: {
    key: 'wall',
    viewBox: { width: 1000, height: 640 },
    pxPerCm: 2.2,
    anchor: { mode: 'hang', centerX: 680, y: 300, maxW: 560, maxH: 480 },
    reference: { label: 'Porte : 2 m' },
    ariaContext: 'accroché sur un grand mur',
    tiltDeg: 0,
  },
};

/**
 * Choisit la scène selon la PLUS GRANDE dimension physique du kit (seuils
 * 45 / 150 cm). On regarde le plus grand côté et pas seulement la largeur :
 * un kit portrait étroit mais haut (ex. 25×75 cm) doit aller au salon, pas
 * sur un bureau où il déborderait (revue V2 — l'échelle reste exacte).
 */
export function pickScene(widthCm: number, heightCm: number = widthCm): SceneKey {
  const w = Number.isFinite(widthCm) && widthCm > 0 ? widthCm : 0;
  const h = Number.isFinite(heightCm) && heightCm > 0 ? heightCm : 0;
  const side = Math.max(w, h);
  if (side <= 0) return 'desk';
  if (side < DESK_MAX_CM) return 'desk';
  if (side <= LIVING_MAX_CM) return 'living';
  return 'wall';
}

/** Spec complète de la scène adaptée aux dimensions d'un kit. */
export function sceneFor(widthCm: number, heightCm: number = widthCm): SceneSpec {
  return SCENES[pickScene(widthCm, heightCm)];
}

export type KitPlacement = {
  /** Coin haut-gauche du kit dans le viewBox (px). */
  x: number;
  y: number;
  width: number;
  height: number;
  /**
   * true si le kit est physiquement plus grand que ce que la scène peut
   * montrer : il est alors clampé (ratio préservé) et l'UI doit afficher
   * la taille réelle en clair.
   */
  overflow: boolean;
};

/**
 * Position et taille du kit dans le viewBox de la scène, à l'échelle exacte
 * (`cm × pxPerCm`). Si le kit déborde de la zone d'accroche, il est réduit
 * uniformément pour tenir — flag `overflow` levé.
 */
export function kitPlacement(scene: SceneSpec, widthCm: number, heightCm: number): KitPlacement {
  const wCm = safeCm(widthCm);
  const hCm = safeCm(heightCm);
  let w = wCm * scene.pxPerCm;
  let h = hCm * scene.pxPerCm;

  const { anchor } = scene;
  const overflow = w > anchor.maxW || h > anchor.maxH;
  if (overflow) {
    const s = Math.min(anchor.maxW / w, anchor.maxH / h);
    w *= s;
    h *= s;
  }

  return {
    x: anchor.centerX - w / 2,
    y: anchor.mode === 'rest' ? anchor.y - h : anchor.y - h / 2,
    width: w,
    height: h,
    overflow,
  };
}

/** Garde-fou : une dimension dégénérée (NaN, ≤ 0) retombe sur 1 cm. */
function safeCm(v: number): number {
  return Number.isFinite(v) && v > 0 ? v : 1;
}
